import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Notify, Loading } from 'notiflix';
import {
  Box,
  Card,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Avatar,
} from '@mui/material';
import { Save, ArrowBack, Add, Delete } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { selectCurrentIndustry } from 'store/slices/industrySlice';
import { selectLoading } from 'store/slices/industrySlice';
import { selectSubmitting } from 'store/slices/industrySlice';
import { industryValidationSchema } from 'schemas/industryValidationSchema';
import { fetchIndustryById, updateIndustry, createIndustry } from 'store/slices/industrySlice';

const IndustryForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const currentIndustry = useSelector(selectCurrentIndustry);
  const loading = useSelector(selectLoading);
  const submitting = useSelector(selectSubmitting);

  const [iconPreview, setIconPreview] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(industryValidationSchema),
    defaultValues: {
      name: '',
      description: '',
      order: 0,
      is_active: true,
      color: '#3f51b5',
      iconFile: null,
    },
  });

  const watchedIconFile = watch('iconFile');

  useEffect(() => {
    if (isEditMode) {
      dispatch(fetchIndustryById(id));
    }
  }, [id, isEditMode, dispatch]);

  useEffect(() => {
    if (isEditMode && currentIndustry) {
      reset({
        name: currentIndustry.name || '',
        description: currentIndustry.description || '',
        order: currentIndustry.order || 0,
        is_active: currentIndustry.is_active !== false,
        color: currentIndustry.color || '#3f51b5',
        iconFile: null,
      });
      if (currentIndustry.icon) {
        setIconPreview(currentIndustry.icon);
      }
    }
  }, [currentIndustry, isEditMode, reset]);

  useEffect(() => {
    if (watchedIconFile instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => setIconPreview(e.target.result);
      reader.readAsDataURL(watchedIconFile);
    }
  }, [watchedIconFile]);

  const handleIconChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setValue('iconFile', file);
    }
  };

  const removeIcon = () => {
    setValue('iconFile', null);
    setIconPreview(null);
  };

  const onSubmit = async (data) => {
    try {
      Loading.circle(isEditMode ? 'Updating industry...' : 'Creating industry...');
      
      if (isEditMode) {
        await dispatch(updateIndustry({ id, industryData: data })).unwrap();
        Notify.success('Industry updated successfully');
      } else {
        await dispatch(createIndustry(data)).unwrap();
        Notify.success('Industry created successfully');
      }
      
      navigate('/industries');
    } catch (error) {
      Notify.failure(error || `Failed to ${isEditMode ? 'update' : 'create'} industry`);
    } finally {
      Loading.remove();
    }
  };

  const handleBack = () => {
    navigate('/industries');
  };

  if (isEditMode && loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
          <Typography variant="h6" ml={2}>Loading industry data...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Box mb={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Back to Industries
          </Button>
        </Box>

        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              {isEditMode ? 'Edit Industry' : 'Create New Industry'}
            </MDTypography>
          </MDBox>

          <MDBox p={3}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDTypography variant="h6" gutterBottom>
                    Basic Information
                  </MDTypography>
                  <Divider />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Industry Name *"
                    {...register('name')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Display Order"
                    type="number"
                    {...register('order')}
                    error={!!errors.order}
                    helperText={errors.order?.message}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    {...register('description')}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MDTypography variant="h6" gutterBottom>
                    Appearance
                  </MDTypography>
                  <Divider />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Color"
                    type="color"
                    {...register('color')}
                    error={!!errors.color}
                    helperText={errors.color?.message}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Icon
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      {iconPreview && (
                        <Avatar src={iconPreview} alt="Icon preview" sx={{ width: 56, height: 56 }} />
                      )}
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<Add />}
                        sx={{ color : "#454545ff"}}
                      >
                        Upload Icon
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleIconChange}
                        />
                      </Button>
                      {iconPreview && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={removeIcon}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                    {errors.iconFile && (
                      <Typography color="error" variant="caption">
                        {errors.iconFile.message}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...register('is_active')}
                        defaultChecked
                      />
                    }
                    label="Active Industry"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      sx={{ color : "#454545ff"}}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                      disabled={submitting}
                      sx={{
                        backgroundColor: 'info.main',
                        '&:hover': {
                          backgroundColor: 'info.dark',
                        },
                      }}
                    >
                      {submitting 
                        ? (isEditMode ? 'Updating...' : 'Creating...')
                        : (isEditMode ? 'Update Industry' : 'Create Industry')
                      }
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default IndustryForm;
